import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Facebook, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const FacebookCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'denied'>('loading');
  const [message, setMessage] = useState('Processing Facebook Pages connection...');
  const [pages, setPages] = useState<any[]>([]);
  
  // Prevent multiple executions of the callback
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Wait for Auth0 to finish loading
    if (isLoading) {
      return;
    }
    
    // Guard against multiple executions
    if (hasProcessed.current) {
      return;
    }
    
    const handleFacebookCallback = async () => {
      try {
        // Check if user is authenticated
        if (!isAuthenticated) {
          console.error('🔴 [FB_CALLBACK] User not authenticated');
          setStatus('error');
          setMessage('You must be logged in to connect Facebook pages. Please log in first.');
          return;
        }
        
        // Mark as processed to prevent duplicate executions
        hasProcessed.current = true;

        const error = searchParams.get('error');
        const code = searchParams.get('code');
        const errorDescription = searchParams.get('error_description');
        const errorReason = searchParams.get('error_reason');

        // Handle user denied permissions
        if (error === 'access_denied' || errorReason === 'user_denied') {
          console.log('🔴 [FB_CALLBACK] User denied Facebook permissions');
          setStatus('denied');
          setMessage('You cancelled the Facebook authorization. To connect your Facebook pages, you need to approve the requested permissions.');
          return;
        }

        // Handle other errors
        if (error) {
          console.error('🔴 [FB_CALLBACK] Facebook OAuth error:', error, errorDescription);
          setStatus('error');
          setMessage(`Facebook authorization failed: ${errorDescription || error}`);
          return;
        }

        // Handle successful authorization
        if (code) {
          console.log('✅ [FB_CALLBACK] Facebook OAuth code received');
          setMessage('Exchanging authorization code for page access tokens...');
          
          try {
            // Get Auth0 token for backend authentication
            const token = await getAccessTokenSilently();
            
            // Exchange the code for access token and pages
            console.log('🔄 [FB_CALLBACK] Calling /facebook/oauth/exchange');
            const response = await axios.post('/facebook/oauth/exchange', 
              {
                code,
                state: searchParams.get('state')
              },
              {
                baseURL: process.env.REACT_APP_API_URL || '',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (response.data.success) {
              console.log('✅ [FB_CALLBACK] Facebook pages connected successfully');
              
              // Check if user needs to complete signup
              if (response.data.requiresSignup) {
                setStatus('success');
                setMessage('Facebook connected! Please complete your profile to continue.');
                console.log('🆕 [FB_CALLBACK] New user, redirecting to signup...');
                
                // Redirect to signup page after short delay
                setTimeout(() => {
                  navigate('/signup?redirect=/accounts');
                }, 2000);
                return;
              }
              
              setStatus('success');
              setMessage('Facebook pages connected successfully!');
              setPages(response.data.pages || []);
              
              // Add a delay to show success message
              console.log('✅ [FB_CALLBACK] Pages connected, redirecting to FacebookPages');
              setTimeout(() => {
                navigate('/accounts');
              }, 2000);
              
            } else {
              setStatus('error');
              setMessage('Failed to connect Facebook pages. Please try again.');
            }
          } catch (exchangeError: any) {
            console.error('❌ [FB_CALLBACK] Exchange error:', exchangeError);
            setStatus('error');
            
            // Handle specific error cases
            if (exchangeError.response?.status === 400) {
              if (exchangeError.response.data.error.includes('No Facebook pages were found')) {
                setMessage('No Facebook pages found. Please make sure you have admin access to at least one Facebook page and try again.');
              } else {
                setMessage(exchangeError.response.data.error || 'Invalid request. Please try again.');
              }
            } else if (exchangeError.response?.status === 401) {
              setMessage('Authentication failed. Please log in again and try connecting your Facebook pages.');
            } else if (exchangeError.response?.status === 500) {
              setMessage('Server error. Please try again later or contact support if the problem persists.');
            } else if (exchangeError.code === 'ERR_NETWORK') {
              setMessage('Cannot connect to server. Please check your internet connection and try again.');
            } else {
              setMessage(`Connection failed: ${exchangeError.response?.data?.error || exchangeError.message}`);
            }
          }
        } else {
          console.error('🔴 [FB_CALLBACK] No authorization code received');
          setStatus('error');
          setMessage('No authorization code received from Facebook.');
        }
      } catch (error: any) {
        console.error('❌ [FB_CALLBACK] Unexpected error:', error);
        setStatus('error');
        setMessage(`An unexpected error occurred: ${error.message}`);
      }
    };

    handleFacebookCallback();
  }, [isLoading, isAuthenticated]);

  const handleRetry = () => {
    navigate('/accounts');
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-16 w-16 animate-spin text-blue-600" />;
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-600" />;
      case 'denied':
        return <XCircle className="h-16 w-16 text-orange-600" />;
      case 'error':
        return <XCircle className="h-16 w-16 text-red-600" />;
      default:
        return <Loader2 className="h-16 w-16 animate-spin text-blue-600" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'denied':
        return 'text-orange-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Connecting...';
      case 'success':
        return 'Success!';
      case 'denied':
        return 'Authorization Cancelled';
      case 'error':
        return 'Connection Failed';
      default:
        return 'Connecting...';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Facebook className="h-6 w-6 text-blue-600" />
              <span>Facebook Pages Connection</span>
            </CardTitle>
            <CardDescription>
              Processing your Facebook pages authorization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                {getStatusIcon()}
              </div>
              
              <div>
                <h3 className={`text-lg font-semibold ${getStatusColor()}`}>
                  {getStatusTitle()}
                </h3>
                <p className="text-gray-600 mt-2">
                  {message}
                </p>
              </div>

              {/* Show connected pages on success */}
              {status === 'success' && pages.length > 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">Connected Pages:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    {pages.map((page, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4" />
                        <span>{page.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col space-y-2 mt-6">
                {status === 'success' && (
                  <Button
                    onClick={() => navigate('/accounts')}
                    className="w-full"
                  >
                    View Connected Pages
                  </Button>
                )}
                
                {(status === 'denied' || status === 'error') && (
                  <Button
                    onClick={handleRetry}
                    className="w-full"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Facebook Pages
                  </Button>
                )}

                {status === 'loading' && (
                  <Button
                    onClick={() => navigate('/accounts')}
                    variant="outline"
                    className="w-full"
                  >
                    Cancel
                  </Button>
                )}
              </div>

              {/* Auto-redirect notice */}
              {status === 'success' && (
                <p className="text-xs text-gray-500 mt-4">
                  You will be redirected automatically in a few seconds...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default FacebookCallbackPage; 